import React, { useState, useEffect } from 'react';
import { Consultant, ConsultantContact, ManagedCustomer, AuthorizationFile, SERVICE_TYPE_LABELS } from '../../types/consultant';
import { updateConsultant, addContactToConsultant, updateContactInConsultant, deleteContactFromConsultant } from '../../services/consultantService';
import { useCustomers } from '../../hooks/useCustomers';
import { Customer } from '../../types/customer';
import { XMarkIcon, PlusIcon, TrashIcon, UserGroupIcon, DocumentTextIcon, PhoneIcon, EnvelopeIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';

interface ConsultantDetailViewProps {
  consultant: Consultant;
  onClose: () => void;
}

const ConsultantDetailView: React.FC<ConsultantDetailViewProps> = ({ consultant, onClose }) => {
  const { customers: allCustomers } = useCustomers();
  const [activeTab, setActiveTab] = useState<'contacts' | 'customers' | 'authorizations'>('contacts');
  const [contacts, setContacts] = useState<ConsultantContact[]>(consultant.contacts);
  const [managedCustomers, setManagedCustomers] = useState<ManagedCustomer[]>(consultant.managedCustomers);
  const [authorizationFiles, setAuthorizationFiles] = useState<AuthorizationFile[]>(consultant.authorizationFiles);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const saveChanges = async () => {
    try {
      setLoading(true);
      await updateConsultant(consultant.id, {
        contacts,
        managedCustomers,
        authorizationFiles
      });
      toast.success('Változtatások mentve!');
      onClose();
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Hiba a mentés során');
    } finally {
      setLoading(false);
    }
  };

  const addContact = () => {
    const newContact: ConsultantContact = {
      id: Date.now().toString(),
      name: '',
      email: '',
      phone: '',
      isPrimary: contacts.length === 0
    };
    setContacts([...contacts, newContact]);
  };

  const updateContact = (id: string, field: keyof ConsultantContact, value: string | boolean) => {
    setContacts(contacts.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const removeContact = (id: string) => {
    const contact = contacts.find(c => c.id === id);
    if (!contact) return;
    
    const confirmed = window.confirm(
      `Biztosan törölni szeretnéd ezt a kapcsolattartót?\n\nNév: ${contact.name}\nEmail: ${contact.email}`
    );
    
    if (confirmed) {
      setContacts(contacts.filter(c => c.id !== id));
      toast.success('Kapcsolattartó törölve!');
    }
  };

  const setPrimaryContact = (id: string) => {
    setContacts(contacts.map(c => ({ ...c, isPrimary: c.id === id })));
  };

  const addCustomer = (customerId: string) => {
    const customer = allCustomers.find(c => c.id === customerId);
    if (!customer) return;

    const alreadyAdded = managedCustomers.some(mc => mc.customerId === customerId);
    if (alreadyAdded) {
      toast.error('Ez az ügyfél már hozzá van rendelve!');
      return;
    }

    const newManagedCustomer: ManagedCustomer = {
      customerId: customer.id,
      customerName: customer.type === 'organization' ? customer.companyName : `${customer.firstName} ${customer.lastName}`,
      serviceType: 'both',
      addedAt: new Date()
    };
    setManagedCustomers([...managedCustomers, newManagedCustomer]);
    toast.success('Ügyfél hozzárendelve!');
  };

  const updateCustomerServiceType = (customerId: string, serviceType: 'electricity' | 'gas' | 'both') => {
    setManagedCustomers(managedCustomers.map(mc => 
      mc.customerId === customerId ? { ...mc, serviceType } : mc
    ));
  };

  const removeCustomer = (customerId: string) => {
    setManagedCustomers(managedCustomers.filter(mc => mc.customerId !== customerId));
    toast.success('Ügyfél eltávolítva!');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingFile(true);
      const storageRef = ref(storage, `consultants/${consultant.id}/authorizations/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const newFile: AuthorizationFile = {
        id: Date.now().toString(),
        fileName: file.name,
        fileUrl: downloadURL,
        uploadedAt: new Date()
      };

      setAuthorizationFiles([...authorizationFiles, newFile]);
      toast.success('Fájl sikeresen feltöltve!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Hiba a fájl feltöltése során');
    } finally {
      setUploadingFile(false);
    }
  };

  const removeFile = (fileId: string) => {
    setAuthorizationFiles(authorizationFiles.filter(f => f.id !== fileId));
    toast.success('Fájl eltávolítva!');
  };

  // Dropdown bezárása külső kattintásra
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCustomerDropdown]);

  const availableCustomers = allCustomers
    .filter(customer => !managedCustomers.some(mc => mc.customerId === customer.id))
    .sort((a, b) => {
      const nameA = a.type === 'organization' ? a.companyName : `${a.firstName} ${a.lastName}`;
      const nameB = b.type === 'organization' ? b.companyName : `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB, 'hu');
    });

  const filteredCustomers = availableCustomers.filter(customer => {
    if (!customerSearchTerm) return true;
    const customerName = customer.type === 'organization' 
      ? customer.companyName 
      : `${customer.firstName} ${customer.lastName}`;
    return customerName.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
           customer.taxNumber?.toLowerCase().includes(customerSearchTerm.toLowerCase());
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full my-8 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                  <BuildingOfficeIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{consultant.companyName}</h2>
                  <div className="flex items-center gap-4 mt-1 text-sm text-white/90">
                    {consultant.taxNumber && (
                      <span className="flex items-center gap-1">
                        <span className="font-medium">Adószám:</span> {consultant.taxNumber}
                      </span>
                    )}
                    {consultant.registrationNumber && (
                      <span className="flex items-center gap-1">
                        <span className="font-medium">Cégjegyzékszám:</span> {consultant.registrationNumber}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {consultant.notes && (
                <div className="mt-3 p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg">
                  <p className="text-sm text-white">
                    <span className="font-semibold">💡 Megjegyzés:</span> {consultant.notes}
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex-1 px-6 py-4 text-sm font-medium border-b-3 transition-all ${
                activeTab === 'contacts'
                  ? 'border-primary-500 bg-white text-primary-700 shadow-sm'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <UserGroupIcon className={`h-5 w-5 ${activeTab === 'contacts' ? 'text-primary-600' : 'text-gray-400'}`} />
                <span>Kapcsolattartók</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === 'contacts' 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {contacts.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`flex-1 px-6 py-4 text-sm font-medium border-b-3 transition-all ${
                activeTab === 'customers'
                  ? 'border-primary-500 bg-white text-primary-700 shadow-sm'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <BuildingOfficeIcon className={`h-5 w-5 ${activeTab === 'customers' ? 'text-primary-600' : 'text-gray-400'}`} />
                <span>Kezelt ügyfelek</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === 'customers' 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {managedCustomers.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('authorizations')}
              className={`flex-1 px-6 py-4 text-sm font-medium border-b-3 transition-all ${
                activeTab === 'authorizations'
                  ? 'border-primary-500 bg-white text-primary-700 shadow-sm'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <DocumentTextIcon className={`h-5 w-5 ${activeTab === 'authorizations' ? 'text-primary-600' : 'text-gray-400'}`} />
                <span>Meghatalmazások</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === 'authorizations' 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {authorizationFiles.length}
                </span>
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Kapcsolattartók</h3>
                <button
                  onClick={addContact}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Új kapcsolattartó
                </button>
              </div>

              {contacts.length === 0 ? (
                <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Még nincs kapcsolattartó hozzáadva</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {contacts.map((contact, index) => (
                    <div key={contact.id} className="p-4 border border-gray-200 rounded-xl bg-gradient-to-br from-white to-gray-50 hover:shadow-lg hover:border-primary-300 transition-all duration-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Kapcsolattartó #{index + 1}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPrimaryContact(contact.id)}
                            className={`text-xs px-2 py-1 rounded ${
                              contact.isPrimary
                                ? 'bg-blue-100 text-blue-800 font-medium'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            }`}
                          >
                            {contact.isPrimary ? '✓ Elsődleges' : 'Elsődlegesnek jelöl'}
                          </button>
                          <button
                            onClick={() => removeContact(contact.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Név</label>
                          <input
                            type="text"
                            placeholder="Név"
                            value={contact.name}
                            onChange={(e) => updateContact(contact.id, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Email</label>
                          <input
                            type="email"
                            placeholder="Email"
                            value={contact.email}
                            onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Telefonszám</label>
                          <input
                            type="tel"
                            placeholder="Telefonszám"
                            value={contact.phone}
                            onChange={(e) => updateContact(contact.id, 'phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-xs text-gray-500 mb-1">Megjegyzés</label>
                        <textarea
                          placeholder="Megjegyzés a kapcsolattartóhoz..."
                          value={contact.notes || ''}
                          onChange={(e) => updateContact(contact.id, 'notes', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Customers Tab */}
          {activeTab === 'customers' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Kezelt ügyfelek</h3>
                <div className="relative customer-search-container">
                  <input
                    type="text"
                    placeholder="Ügyfél keresése név vagy adószám alapján..."
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="w-full min-w-[500px] px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredCustomers.map(customer => (
                        <button
                          key={customer.id}
                          onClick={() => {
                            addCustomer(customer.id);
                            setCustomerSearchTerm('');
                            setShowCustomerDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">
                              {customer.type === 'organization' ? customer.companyName : `${customer.firstName} ${customer.lastName}`}
                            </span>
                            {customer.taxNumber && (
                              <span className="text-xs text-gray-500 ml-2">{customer.taxNumber}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showCustomerDropdown && filteredCustomers.length === 0 && customerSearchTerm && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-4 text-center text-sm text-gray-500">
                      Nincs találat
                    </div>
                  )}
                </div>
              </div>

              {managedCustomers.length === 0 ? (
                <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                  <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Még nincs ügyfél hozzárendelve</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {managedCustomers.map((mc) => (
                    <div key={mc.customerId} className="p-4 border border-gray-200 rounded-xl bg-gradient-to-br from-white to-gray-50 hover:shadow-lg hover:border-primary-300 transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{mc.customerName}</p>
                              <p className="text-xs text-gray-500">
                                Hozzáadva: {new Date(mc.addedAt).toLocaleDateString('hu-HU')}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <select
                            value={mc.serviceType}
                            onChange={(e) => updateCustomerServiceType(mc.customerId, e.target.value as any)}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value="electricity">Áram</option>
                            <option value="gas">Gáz</option>
                            <option value="both">Áram és Gáz</option>
                          </select>
                          <button
                            onClick={() => removeCustomer(mc.customerId)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Authorizations Tab */}
          {activeTab === 'authorizations' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Meghatalmazások</h3>
                <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 cursor-pointer">
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {uploadingFile ? 'Feltöltés...' : 'Fájl feltöltése'}
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={uploadingFile}
                  />
                </label>
              </div>

              {authorizationFiles.length === 0 ? (
                <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Még nincs meghatalmazás feltöltve</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {authorizationFiles.map((file) => (
                    <div key={file.id} className="p-4 border border-gray-200 rounded-xl bg-gradient-to-br from-white to-gray-50 hover:shadow-lg hover:border-primary-300 transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{file.fileName}</p>
                            <p className="text-xs text-gray-500">
                              Feltöltve: {new Date(file.uploadedAt).toLocaleDateString('hu-HU')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={file.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            Letöltés
                          </a>
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Bezárás
          </button>
          <button
            onClick={saveChanges}
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {loading ? 'Mentés...' : 'Változtatások mentése'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsultantDetailView;
